import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { FaCalendar, FaClock, FaArrowLeft, FaShare } from 'react-icons/fa';

import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL;
const UPLOADS_BASE_URL = process.env.REACT_APP_API_URL?.replace(
  '/backend/api',
  '/uploads',
);

import WithBackground from '../Common/WithBackground';
import Footer from '../Footer';
import Header from '../Header';

import './style.css';

const NewsDetail = ({ bg, overlay }) => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Generate news list link based on current language
  const getNewsListLink = () => {
    const language = i18n.language || 'pl';

    const langPaths = {
      pl: '/aktualnosci',
      rus: '/rus/novosti',
      ua: '/ua/novyny',
      en: '/en/news',
    };

    return langPaths[language] || langPaths.pl;
  };

  // Generate news detail link based on current language
  const getNewsDetailLink = (newsId) => {
    const language = i18n.language || 'pl';

    const langPaths = {
      pl: '/aktualnosci',
      rus: '/rus/novosti',
      ua: '/ua/novyny',
      en: '/en/news',
    };

    return `${langPaths[language] || langPaths.pl}/${newsId}`;
  };

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${API_BASE_URL}/news.php?id=${id}&lang=${i18n.language}`,
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (!data.success || !data.news) {
          throw new Error('Article not found');
        }

        const processedArticle = {
          ...data.news,
          ...(data.news.translations[i18n.language] ||
            data.news.translations.pl),
        };

        setArticle(processedArticle);

        // Fetch related news (exclude current article)
        const relatedResponse = await fetch(
          `${API_BASE_URL}/news.php?lang=${i18n.language}&limit=3`,
        );
        if (relatedResponse.ok) {
          const relatedData = await relatedResponse.json();
          if (relatedData.success && relatedData.news) {
            // Filter out current article
            const filtered = relatedData.news.filter(
              (item) => item.id !== parseInt(id),
            );
            setRelatedNews(filtered.slice(0, 3));
          }
        }
      } catch (error) {
        console.error('Error fetching article:', error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchArticle();
    }
  }, [id, i18n.language]);

  // Format date for display
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const language = i18n.language || 'pl';

      const options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      };

      return date.toLocaleDateString(
        language === 'pl'
          ? 'pl-PL'
          : language === 'rus'
            ? 'ru-RU'
            : language === 'ua'
              ? 'uk-UA'
              : 'en-US',
        options,
      );
    } catch (error) {
      return dateString;
    }
  };

  // Share functionality
  const shareArticle = () => {
    if (navigator.share && article) {
      navigator.share({
        title: article.title,
        text: article.excerpt || article.title,
        url: window.location.href,
      });
    } else {
      // Fallback - copy URL to clipboard
      navigator.clipboard.writeText(window.location.href);
    }
  };

  if (loading) {
    return (
      <div className='news-detail'>
        <Header />
        <main className='news-detail__main'>
          <div className='news-detail__container'>
            <div className='news-detail__loading'>
              <p>{t('common.loading')}</p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className='news-detail'>
        <Header />
        <main className='news-detail__main'>
          <div className='news-detail__container'>
            <div className='news-detail__error'>
              <h1>{t('common.error')}</h1>
              <p>{t('news.articleNotFound') || 'Article not found'}</p>
              <Link to={getNewsListLink()} className='btn'>
                {t('news.backToNews')}
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const newsDetailContent = (
    <div className='news-detail'>
      <Header />
      <main className='news-detail__main'>
        <div className='news-detail__container'>
          {/* Back Navigation */}
          <div className='news-detail__back'>
            <Link to={getNewsListLink()} className='news-detail__back-link'>
              <FaArrowLeft />
              {t('news.backToNews')}
            </Link>
          </div>

          {/* Article Header */}
          <header className='news-detail__header'>
            {article.is_featured && (
              <div className='news-detail__badge'>{t('news.featured')}</div>
            )}

            <h1 className='news-detail__title'>{article.title}</h1>

            <div className='news-detail__meta'>
              <div className='news-detail__date'>
                <FaCalendar />
                <span>
                  {t('news.publishedOn')} {formatDate(article.created_at)}
                </span>
              </div>

              <button
                className='news-detail__share'
                onClick={shareArticle}
                title='Share article'
              >
                <FaShare />
              </button>
            </div>

            {article.excerpt && (
              <p className='news-detail__excerpt'>{article.excerpt}</p>
            )}
          </header>

          {/* Article Image */}
          {article.image && (
            <div className='news-detail__image'>
              <img
                src={`${UPLOADS_BASE_URL}/${article.image}`}
                alt={article.title}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Article Content */}
          <div className='news-detail__content'>
            <div className='news-detail__text'>
              {article.content &&
                article.content
                  .split('\n')
                  .map((line, index) => {
                    const trimmedLine = line.trim();
                    if (!trimmedLine) return null;

                    // Check if line starts with emoji or bullet point
                    const emojiRegex = /^[\p{Emoji}\p{Emoji_Component}▪▫✔]/u;
                    const isHeading = emojiRegex.test(trimmedLine);

                    // Convert URLs to clickable links
                    const urlRegex = /(https?:\/\/[^\s]+)/g;
                    const parts = trimmedLine.split(urlRegex);

                    return (
                      <p
                        key={index}
                        className={`news-detail__paragraph ${isHeading ? 'news-detail__paragraph--heading' : ''}`}
                      >
                        {parts.map((part, partIndex) => {
                          if (urlRegex.test(part)) {
                            return (
                              <a
                                key={partIndex}
                                href={part}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='news-detail__link'
                              >
                                {part}
                              </a>
                            );
                          }
                          return part;
                        })}
                      </p>
                    );
                  })
                  .filter(Boolean)}
            </div>
          </div>

          {/* Related News */}
          {relatedNews.length > 0 && (
            <section className='news-detail__related'>
              <h2 className='news-detail__related-title'>
                {t('news.relatedNews')}
              </h2>

              <div className='news-detail__related-grid'>
                {relatedNews.map((relatedArticle) => (
                  <article
                    key={relatedArticle.id}
                    className='news-detail__related-card'
                  >
                    <Link to={getNewsDetailLink(relatedArticle.id)}>
                      <div className='news-detail__related-image'>
                        <img
                          src={`${UPLOADS_BASE_URL}/${relatedArticle.image}`}
                          alt={relatedArticle.title}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const placeholder = e.target.nextSibling;
                            if (placeholder) {
                              placeholder.style.display = 'flex';
                            }
                          }}
                        />
                        <div className='news-detail__related-placeholder'>
                          <FaClock />
                        </div>
                      </div>

                      <div className='news-detail__related-content'>
                        <h3 className='news-detail__related-card-title'>
                          {relatedArticle.title}
                        </h3>

                        <div className='news-detail__related-date'>
                          <FaCalendar />
                          {formatDate(relatedArticle.created_at)}
                        </div>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );

  return bg ? (
    <WithBackground bg={bg} overlay={overlay}>
      {newsDetailContent}
    </WithBackground>
  ) : (
    newsDetailContent
  );
};

NewsDetail.propTypes = {
  bg: PropTypes.string,
  overlay: PropTypes.oneOf(['dark', 'light', 'golden', 'none']),
};

NewsDetail.defaultProps = {
  bg: null,
  overlay: 'dark',
};

export default NewsDetail;
